import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import DasboardService from "../../../services/DashboardService";
import auth from "../../../services/auth";
import {
  PRE_SIM_RATING_QUESTIONS,
  POST_SIM_RATING_QUESTIONS,
  PIE_CHART_COLORS,
  truncateText,
  getGroupAccountTypesByEducationLevel,
  Wellness_RATING_QUESTIONS,
} from "../../../utils/utils";
import ChartToolTip from "./ChartTooltip.jsx";
import { ACCOUNT_TYPE } from "../../../utils/constants";
import DataPieChart from "../../../components/PieChart";

const chartDivStyle =
  "col-12 col-md-6 d-flex flex-column align-items-center mb-4";
const chartHeading = "sub-heading m-3 text-left";

// Custom Y-Axis Tick Component
const YAxisLabel = ({ x, y, payload }) => {
  const text = payload.value;
  const maxTextLength = 25; // Adjust max length as needed
  const truncatedText = truncateText(text, maxTextLength);

  return (
    <g transform={`translate(${x},${y})`}>
      <title>{text}</title> {/* Tooltip on hover */}
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fontSize={14}
        fill="#000"
        className="bar_chart_ellipsize"
      >
        {truncatedText}
      </text>
    </g>
  );
};

const BarGraph = (showFeedbackCharts, data, chartHeight) => {
  return (
    <>
      {!showFeedbackCharts ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
            barCategoryGap="30%"
          >
            <XAxis type="number" domain={[0, 5]} hide />
            <YAxis
              dataKey="name"
              type="category"
              width={200}
              tick={<YAxisLabel />} // Use Custom Tick Component
            />
            <Tooltip content={<ChartToolTip prefix="Average:" />} />
            <Bar
              dataKey="value"
              fill={PIE_CHART_COLORS[0]}
              barSize={Math.max(30, window.innerWidth * 0.05)}
              radius={[5, 5, 5, 5]}
            >
              <LabelList
                dataKey="value"
                position="right"
                fill="black"
                fontSize={14}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="accreditation-msg">No Data Exists</div>
      )}
    </>
  );
};

const CustomBarChart = (props) => {
  const [preRatings, setPreRatings] = useState([]);
  const [postRatings, setPostRatings] = useState([]);
  const [wellnessRating, setWellnessRating] = useState([]);
  const [jobConfidenceRating, setJobConfidenceRating] = useState([]);
  const [showFeedbackCharts, setShowFeedbackCharts] = useState(true);
  const [showWellnessChart, setShowWellnessChart] = useState(true);
  const [showJobConfidenceChart, setShowJobConfidenceChart] = useState(true);
  const [yesNoData, setYesNoData] = useState([]);
  const [isDataForChart, setIsDataForChart] = useState(false);

  const authenticatedUserDetail = auth.getUserDetails();

  const loadData = async () => {
    const ratings =
      await DasboardService().getPrePostSimFeedbackRatingQuestionsAverageByAccount(
        authenticatedUserDetail?.accId
      );

    const percent = await DasboardService().getWellnessSurveyQ6YesNoPercentage({
      accId: authenticatedUserDetail?.accId,
      accType: props.accountType,
    });

    setIsDataForChart(percent?.data?.data.every((item) => item.value === null));
    const wellnessSurveyRatingResponse =
      await DasboardService().getWellnessSurveyRatingQuestionsAverageByAccount({
        accId: authenticatedUserDetail?.accId,
        accType: props.accountType,
      });

    const jobConfidenceSurveyRatingResponse =
      await DasboardService().getJobConfidenceSurveyRatingQuestionsAverageByAccount(
        { accId: authenticatedUserDetail?.accId, accType: props.accountType }
      );

    const transformedPreFeedbackRatings = Object.values(
      ratings?.data?.preSimFeedbackAvg
    ).map((value, index) => ({
      name: PRE_SIM_RATING_QUESTIONS[`preq${index + 2}`],
      value: value,
    }));

    const transformedPostFeedbackRatings = Object.values(
      ratings?.data?.postSimFeedbackAvg
    ).map((value, index) => ({
      name: POST_SIM_RATING_QUESTIONS[`postq${index + 2}`],
      value: value,
    }));

    const transformedWellnessRatings = [];
    const wellnessRating = wellnessSurveyRatingResponse?.data?.data;

    Object.entries(Wellness_RATING_QUESTIONS).forEach(([key, value]) => {
      transformedWellnessRatings.push({
        name: value,
        value: wellnessRating[key],
      });
    });

    const transformedJobConfidenceRatings = Object.values(
      jobConfidenceSurveyRatingResponse?.data?.data
    ).map((value, index) => ({
      name: PRE_SIM_RATING_QUESTIONS[`preq${index + 2}`],
      value: value,
    }));

    setShowFeedbackCharts(
      Object.values(ratings?.data?.postSimFeedbackAvg).every((val) => val === 0)
    );

    setShowWellnessChart(
      Object.values(wellnessRating).every((val) => val === 0)
    );

    setShowJobConfidenceChart(
      Object.values(jobConfidenceSurveyRatingResponse?.data?.data).every(
        (val) => val === 0
      )
    );

    setPreRatings(transformedPreFeedbackRatings);
    setPostRatings(transformedPostFeedbackRatings);
    setWellnessRating(transformedWellnessRatings);
    setJobConfidenceRating(transformedJobConfidenceRatings);
    setYesNoData(percent?.data?.data);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <>
      <div className="d-flex flex-wrap justify-content-center w-100">
        {/* Pre-Sim Feedback Chart */}
        <>
          {props.accountType !== ACCOUNT_TYPE.HIGHER_ED && (
            <div className={chartDivStyle}>
              <h3
                className={chartHeading}
              >{`${getGroupAccountTypesByEducationLevel(
                props.accountType
              )} Learning Culture Pre-Sim Feedback`}</h3>
              {BarGraph(showFeedbackCharts, preRatings, 400)}
            </div>
          )}
          {props.accountType !== ACCOUNT_TYPE.HIGHER_ED && (
            <div className={chartDivStyle}>
              <h3
                className={chartHeading}
              >{`${getGroupAccountTypesByEducationLevel(
                props.accountType
              )} Learning Culture Post-Sim Feedback`}</h3>
              {BarGraph(showFeedbackCharts, postRatings, 550)}
            </div>
          )}

          {/* Wellness Survey Chart */}
          <div className={chartDivStyle}>
            <h3 className={chartHeading}>Learning Culture Wellness Survey</h3>
            {BarGraph(showWellnessChart, wellnessRating, 475)}
          </div>

          <div className={`${chartDivStyle} mt-3`}>
            <p className="text-center">
              Have you experienced significant stress, anxiety, or burnout this
              week?
            </p>
            <DataPieChart
              data={yesNoData}
              isDataForChart={isDataForChart}
              legendWidth={false}
            />
          </div>
        </>
      </div>
      {props?.accountType !== ACCOUNT_TYPE.HIGHER_ED && (
        <div className={chartDivStyle}>
          <h3 className={chartHeading}>
            Learning Culture Job Confidence Survey
          </h3>
          {BarGraph(showJobConfidenceChart, jobConfidenceRating, 400)}
        </div>
      )}
    </>
  );
};

export default CustomBarChart;
